import { GameState, Player } from '../types/game';
import { makeOrderFinalizedEvent, makePlayerReadyEvent } from '../domain/events';
import { assignPlayerColors } from '../utils/playerColors';
import { withDefaultStatus } from './reducerHelpers';
import { makeEmptyDiceMask } from '../domain/gameRules';

export function handleToggleReady(state: GameState, playerId: string, ready: boolean): GameState {
  if (state.mode === 'REAL' || !state.lobby) return state;
  const current = state.lobby.readyMap[playerId];
  if (current === ready) return state;
  const ev = makePlayerReadyEvent(playerId, ready);
  return {
    ...state,
    lobby: { ...state.lobby, readyMap: { ...state.lobby.readyMap, [playerId]: ready } },
    events: [...(state.events || []), ev],
  };
}

export function handleInitOnlineLobby(state: GameState, action: { gameId?: string; groupToken?: string | null; groupId?: string | null }): GameState {
  const incomingId = action.gameId && action.gameId.trim().length > 0 ? action.gameId.trim().toUpperCase() : null;
  const hasLocalLobbyPlayers = state.mode === 'ONLINE' && state.id && state.players.length > 0;
  const targetsCurrentGame = !incomingId || incomingId === state.id;
  if (hasLocalLobbyPlayers && targetsCurrentGame) {
    return {
      ...state,
      groupId: action.groupId ?? state.groupId ?? null,
      groupToken: action.groupToken ?? state.groupToken ?? null,
      lobby: state.lobby || { phase: 'LOBBY', readyMap: {}, nextGameParticipation: {} },
    } as GameState;
  }
  const hasOnlineSignature = state.mode === 'ONLINE' && state.id && (state.events || []).some((e) => (
    e.type === 'GAME_CREATED' || e.type === 'ADD_PLAYER' || e.type === 'ORDER_FINALIZED'
  ));
  if (hasOnlineSignature) return state;
  return {
    ...state,
    id: incomingId || state.id || '',
    mode: 'ONLINE' as const,
    groupId: action.groupId ?? state.groupId ?? null,
    groupToken: action.groupToken ?? state.groupToken ?? null,
    lobby: { phase: 'LOBBY' as const, readyMap: {}, nextGameParticipation: {} },
    players: [],
    spectators: [],
  } as GameState;
}

export function handleFinalizeOrder(state: GameState, order: string[]): GameState {
  if (state.mode === 'REAL' || !state.lobby) return state;
  if (state.turnCycle || state.lobby.phase === 'IN_GAME') return state;
  const playersMap = new Map(state.players.map((p) => [p.id, p] as const));
  const seen = new Set<string>();
  const reordered: Player[] = [];
  order.forEach((id) => {
    const p = playersMap.get(id);
    if (!p || seen.has(id)) return;
    seen.add(id);
    reordered.push({ ...p, position: reordered.length + 1 });
  });
  const remaining = state.players
    .filter((p) => !seen.has(p.id))
    .map((p, idx) => ({ ...p, position: reordered.length + idx + 1 }));
  const normalizedOrder = [...reordered, ...remaining].map((p) => p.id);
  const nextPlayers = [...reordered, ...remaining];
  if (nextPlayers.length === 0) return state;
  const ev = makeOrderFinalizedEvent(normalizedOrder);
  return {
    ...state,
    players: nextPlayers,
    lobby: { ...state.lobby, order: normalizedOrder, phase: 'ORDER_REVEAL' },
    events: [...(state.events || []), ev],
  };
}

export function handleBeginOnlineGame(state: GameState, firstPlayerId: string): GameState {
  if (state.mode === 'REAL' || !state.lobby || state.lobby.phase !== 'ORDER_REVEAL' || !state.lobby.order?.length) return state;
  const firstId = firstPlayerId;
  const firstPlayerIndex = state.players.findIndex((p) => p.id === firstId);
  return {
    ...state,
    lobby: { ...state.lobby, phase: 'IN_GAME' as const },
    turnCycle: { activePlayerId: firstId, rollCount: 0, dice: [], kept: makeEmptyDiceMask(), capturedMask: makeEmptyDiceMask(), status: 'IDLE' as const, diceOrigins: [] },
    currentPlayerIndex: firstPlayerIndex >= 0 ? firstPlayerIndex : 0,
    turnNumber: 1,
  };
}

export function handleSetLobbyPhase(state: GameState, phase: 'LOBBY' | 'ORDER_REVEAL' | 'IN_GAME'): GameState {
  if (state.mode === 'REAL' || !state.lobby) return state;
  return { ...state, lobby: { ...state.lobby, phase } };
}

export function handleMergeExternalPlayers(state: GameState, incoming: Player[]): GameState {
  if (state.lobby?.phase === 'IN_GAME') return state;
  const newPlayers = incoming.filter((p) => !state.players.some((sp) => sp.id === p.id));
  if (newPlayers.length === 0) return state;
  const normalizedExisting = state.players.map(withDefaultStatus);
  const mergedIncoming = newPlayers.map((p, idx) => withDefaultStatus({ ...p, position: state.players.length + idx + 1 }));
  const merged = [...normalizedExisting, ...mergedIncoming];
  const nextLobby = state.mode === 'ONLINE' && state.lobby
    ? {
        ...state.lobby,
        readyMap: mergedIncoming.reduce<Record<string, boolean>>((acc, player) => {
          if (typeof acc[player.id] !== 'boolean') {
            acc[player.id] = player.isBot === true;
          }
          return acc;
        }, { ...state.lobby.readyMap }),
      }
    : state.lobby;
  return { ...state, players: assignPlayerColors(merged), lobby: nextLobby };
}
